import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { setupApi, type AddProviderInput, type ProviderCredentialInput } from '../api/setupApi';
import { setupKeys } from '../queryKeys';

export function useProviderCredentials() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: setupKeys.providers(locationId),
    queryFn: () => setupApi.listProviderCredentials(locationId),
    enabled: !!locationId,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: setupKeys.providers(locationId) });
    queryClient.invalidateQueries({ queryKey: setupKeys.readiness(locationId) });
  }

  const saveProvider = useMutation({
    mutationFn: ({
      staffMemberId,
      ...input
    }: Omit<ProviderCredentialInput, 'locationId'> & { staffMemberId: string }) =>
      setupApi.saveProviderCredential(staffMemberId, { ...input, locationId }),
    onSuccess: invalidate,
  });

  const addProvider = useMutation({
    mutationFn: (input: Omit<AddProviderInput, 'locationId'>) =>
      setupApi.addProvider({ ...input, locationId }),
    onSuccess: invalidate,
  });

  return { ...query, providers: query.data ?? [], saveProvider, addProvider, locationId };
}
